import { useEffect, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { EntityList } from "@/components/shared/EntityList";
import { CollectionCard } from "@/components/collections/CollectionCard";
import { COLLECTION_GRID_CLASS } from "@/components/collections/CollectionGrid";
import { TagPill } from "@/components/TagPill";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { ScopeTabs } from "@/components/shared/ScopeTabs";
import { useIsTabActive } from "@/contexts/TabActivityContext";
import { STASHES } from "@/routes";
import { isSuggestedTag } from "@/lib/tagScope";
import { ALL_PREVIEW, FACET_TITLES, SEARCH_FACETS, offersExplore, type ResultFacet, type SearchFacet } from "./searchModel";
import { useSearchKinds } from "./useSearchSources";
import { CARD_TITLE, META } from "@/lib/typography";
import { cn } from "@/lib/utils";

const RESULT_FACETS = SEARCH_FACETS.filter((facet): facet is ResultFacet => facet !== "all");

/**
 * What a query found in the account, by kind (native StashSearchResults): a tab per
 * kind with its count once final; All previews each kind with See all, a kind's own tab
 * lists all of it. The feed draws stashes, so they look and behave as every other stash
 * list; the rest are drawn as they are elsewhere (Stuff rows, collection cards, tag pills).
 */
export function SearchResults({
  query,
  facet,
  stashes,
  onFacet,
  onExplore,
}: {
  query: string;
  facet: SearchFacet;
  stashes: {
    /** What the facet shows (All's first page, or every loaded page), drawn by the feed. */
    view: ReactNode;
    count: number;
    /** Reading the first page (or re-reading). */
    loading: boolean;
    /** No more to read: the count is final. */
    complete: boolean;
    /** Past All's preview, loaded or not. */
    more: boolean;
  };
  onFacet: (facet: SearchFacet) => void;
  onExplore: () => void;
}) {
  const navigate = useNavigate();
  const { stuff, collections, tags, counts, complete, collectionsQuery, tagsQuery } = useSearchKinds(query, stashes);
  const shows = (kind: ResultFacet) => facet === "all" || facet === kind;
  const preview = <T,>(kind: ResultFacet, items: T[]) => (facet === "all" ? items.slice(0, ALL_PREVIEW[kind]) : items);
  const more: Record<ResultFacet, boolean> = {
    stashes: stashes.more,
    stuff: stuff.ids.length > ALL_PREVIEW.stuff || !!stuff.hasNextPage,
    collections: collections.length > ALL_PREVIEW.collections,
    tags: tags.length > ALL_PREVIEW.tags,
  };
  const stuffIds = preview("stuff", stuff.ids);
  // Rendered EntityHeader rows already read/batch the shared entity units. A second
  // list-level hydration would request the same entities again, including hidden facets.

  const heading = (kind: ResultFacet) => (
    <SectionHeader
      title={FACET_TITLES[kind]}
      action={
        facet === "all" && more[kind] ? (
          <Button
            type="button"
            onClick={() => onFacet(kind)}
            aria-label={`See all ${FACET_TITLES[kind]}`}
            variant="ghost"
            size="control"
          >
            See all
          </Button>
        ) : undefined
      }
    />
  );

  const nothing =
    facet === "all"
      ? RESULT_FACETS.every(kind => complete[kind] && counts[kind] === 0)
      : complete[facet] && counts[facet] === 0;

  return (
    <div className="flex w-full flex-col gap-5">
      <ScopeTabs
        className="border-b border-border"
        label="Results"
        value={facet}
        onChange={onFacet}
        tabs={SEARCH_FACETS.map(value => ({
          value,
          label: FACET_TITLES[value],
          count: value !== "all" && complete[value] ? counts[value] : undefined,
        }))}
      />

      {shows("stashes") && counts.stashes > 0 && (
        <section>
          {heading("stashes")}
          {stashes.view}
        </section>
      )}
      {shows("stashes") && counts.stashes === 0 && stashes.loading && <Searching kind="stashes" />}

      {shows("stuff") && stuffIds.length > 0 && (
        <section>
          {heading("stuff")}
          <EntityList entityIds={stuffIds} layout="rows" />
        </section>
      )}
      {/* All is a bounded preview; only the Stuff tab continues its own list. */}
      {facet === "stuff" && (
        <Continuation
          more={!!stuff.hasNextPage}
          loading={stuff.isFetching}
          onMore={() => void stuff.fetchNextPage()}
          label="Searching Stuff"
        />
      )}
      {shows("stuff") && stuff.isError && <ReadError kind="stuff" onRetry={() => void stuff.refetch()} />}

      {shows("collections") && collections.length > 0 && (
        <section>
          {heading("collections")}
          <div className={COLLECTION_GRID_CLASS}>
            {preview("collections", collections).map(collection => (
              <CollectionCard key={collection.collection_id} collection={collection} />
            ))}
          </div>
        </section>
      )}

      {shows("tags") && tags.length > 0 && (
        <section>
          {heading("tags")}
          <div className="flex flex-wrap gap-2">
            {preview("tags", tags).map(tag => (
              <TagPill
                key={tag.id}
                name={tag.name}
                ariaLabel={isSuggestedTag(tag) ? `${tag.name}, suggested tag` : tag.name}
                color={tag.color}
                suggested={isSuggestedTag(tag)}
                count={tag.count}
                onClick={() => navigate(`${STASHES()}?tag=${encodeURIComponent(tag.id)}`)}
              />
            ))}
          </div>
        </section>
      )}

      {nothing && (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <p className={cn(CARD_TITLE, "text-foreground")}>No matches</p>
          {facet !== "all" && (
            <Button type="button" variant="secondary" size="sm" onClick={() => onFacet("all")}>
              Show all results
            </Button>
          )}
        </div>
      )}

      {shows("collections") && collectionsQuery.isLoading && <Searching kind="collections" />}
      {shows("collections") && collectionsQuery.isError && <ReadError kind="collections" onRetry={() => void collectionsQuery.refetch()} />}
      {shows("tags") && tagsQuery.isLoading && <Searching kind="tags" />}
      {shows("tags") && tagsQuery.isError && <ReadError kind="tags" onRetry={() => void tagsQuery.refetch()} />}

      {offersExplore(facet, counts, complete) && (
        <Button type="button" variant="secondary" size="sm" className="self-center" onClick={onExplore}>
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          Explore results for “{query.trim()}”
        </Button>
      )}
    </div>
  );
}

function Searching({ kind }: { kind: ResultFacet }) {
  return <p className={cn(META, "loading-dots w-full py-4 text-center")}>Searching {FACET_TITLES[kind]}</p>;
}

/** Reads the next page when it comes into view (native SearchContinuation). */
export function Continuation({
  more,
  loading,
  onMore,
  label,
}: {
  more: boolean;
  loading: boolean;
  onMore: () => void;
  label: string;
}) {
  const { elementRef, isIntersecting } = useIntersectionObserver({ rootMargin: "200px" });
  const active = useIsTabActive();
  useEffect(() => {
    if (active && isIntersecting && more && !loading) onMore();
  }, [active, isIntersecting, more, loading, onMore]);
  // The sentinel stays mounted while a page loads, so "in view" is always its own.
  return (
    <>
      {loading && <p className={cn(META, "loading-dots w-full py-4 text-center")}>{label}</p>}
      {more && <div ref={elementRef} className="h-1 w-full" />}
    </>
  );
}

function ReadError({ kind, onRetry }: { kind: ResultFacet; onRetry: () => void }) {
  return (
    <div className={cn(META, "flex items-center justify-between gap-3 rounded border border-destructive/40 bg-destructive/5 p-4 text-destructive")}>
      <p>Couldn't search {FACET_TITLES[kind]}.</p>
      <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
